/**
 *
 * Students
 *
 */

import { useReducer, useEffect, useRef } from 'react';
import produce from 'immer';
import useDialog from 'containers/Dialog/useDialog.js';
import getStudentStates from './getStudentStates.js';
import useSaveMeetings from '../../hooks/useSaveMeetings.js';

function useSelectStudents(meetingSet) {
  const changed = useRef(false);
  const refState = useRef({}); // For saving state on closing
  const { dlgConfirm } = useDialog();
  const reducer = produce((draft, action) => {
    const { type, id, meetingId, value } = action;
    switch (type) {
      case 'change':
        draft[id].meetings = value;
        break;
      case 'selectType': {
        const mtIdx = draft[id].meetings.findIndex((mt) => mt.id === meetingId);
        draft[id].meetings[mtIdx].meetingLocationType = value;
        break;
      }
      case 'check':
        draft[id].checked = value;
        break;
      case 'checkAll':
        Object.keys(draft).forEach((key) => {
          draft[key].checked = value;
        });
        break;
      default:
    }
    changed.current = true;
  });

  const [state, dispatch] = useReducer(reducer, getStudentStates(meetingSet));
  refState.current = state;
  const saveMeetings = useSaveMeetings();

  useEffect(() => {
    // If inconsistent because of removed students
    const list = meetingSet?.meetings?.list;
    if (!list) {
      return;
    }
    const l1 = list.length;
    const l2 = listMeetings().length;
    if (l1 !== l2) {
      handleSave(true);
    }
  }, [meetingSet.meetings.list]); // eslint-disable-line react-hooks/exhaustive-deps

  function listMeetings() {
    return Object.values(refState.current).filter((st) => st.checked).flatMap((st) => st.meetings);
  }

  function handleChange(id, meetings) {
    dispatch({ type: 'change', id, value: meetings });
  }

  function handleCheck(id, value) {
    const type = id ? 'check' : 'checkAll';
    dispatch({ type, id, value });
  }

  function handleSelectType(id, meetingId, value) {
    dispatch({ type: 'selectType', id, meetingId, value });
  }

  function handleIgnore() {
    changed.current = false;
  }

  async function handleSave(force, prompt) {
    if (!changed.current && !force) {
      return null;
    }
    changed.current = false;
    if (prompt) {
      const response = await dlgConfirm({ message: prompt, yesNo: true }).catch((v) => v);
      if (!response) {
        return null;
      }
    }
    return saveMeetings(meetingSet.id, listMeetings());
  }

  const allChecked = Object.values(state).every((s) => !s.canChange || s.checked);

  return { state, handleChange, handleIgnore, handleCheck, handleSelectType, allChecked, handleSave };
}

export default useSelectStudents;
